import { Inject, Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { OKTA_AUTH } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { ApigeeAuthService } from "./apigee-service";
import { AuthServiceService } from "../auth-service/auth-service.service";
import { SnackBarService } from "../snackbar-service/snack-bar.service";
@Injectable()
export class Interceptor implements HttpInterceptor {
  private jwtRequest$: Observable<any> | null = null;
  private isRetrying = false;
  constructor(
    private apigeeAuthService: ApigeeAuthService,
    private authService: AuthServiceService,
    private snackbarService: SnackBarService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
  ) {}
  private getApigeeRequest(request: HttpRequest<any>): HttpRequest<any> {
    const idToken = this.oktaAuth.getIdToken();
    const accessToken = this.oktaAuth.getAccessToken();
    const authorization = `Bearer ${idToken}, Bearer ${accessToken}`;
    return request.clone({
      setHeaders: {
        Authorization: authorization,
        Accept: "application/json",
      },
    });
  }

  private getTokenRequest(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private getJwtToken(): Observable<any> {
    if (!this.jwtRequest$) {
      const tokenObservable = (this.authService && typeof (this.authService as any).getApigeeToken === 'function')
        ? (this.authService as any).getApigeeToken()
        : this.apigeeAuthService.getAccessToken();

      this.jwtRequest$ = tokenObservable.pipe(
        finalize(() => {
          this.jwtRequest$ = null;
        }),
      );
    }

    return this.jwtRequest$ as Observable<any>;
  }

  private getJwtExchangeRequest(req: HttpRequest<any>): HttpRequest<any> {
    const idToken = this.oktaAuth.getIdToken();
    const accessToken = this.oktaAuth.getAccessToken();
    if (!idToken || !accessToken) {
      return req;
    }
    const authorization = `Bearer ${idToken}, Bearer ${accessToken}`;

    return req.clone({
      setHeaders: {
        Authorization: authorization,
        Accept: "application/json",
      },
    });
  }

  private sendEpcisRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const currentToken = this.apigeeAuthService.getCurrentToken();

    const tokenValid = !!currentToken && this.apigeeAuthService.isTokenValid();
    const tokenIsJwt = !!currentToken && currentToken.startsWith("eyJ");
    if (tokenValid && tokenIsJwt) {
      return next.handle(this.getTokenRequest(request, currentToken!)).pipe(
        catchError((error) => {
          if (error?.status === 401) {
            return this.refreshJwtAndRetry(request, next, error);
          }
          return throwError(() => error);
        }),
      );
    }
    return this.getJwtToken().pipe(
      switchMap((tokenResponse: any) => {
        const token = tokenResponse?.access_token;
        const expiresIn = parseInt(tokenResponse?.expires_in || "0", 10);

        if (!token) {

          return throwError(
            () => new Error("JWT access_token was not returned"),
          );
        }
        this.apigeeAuthService.setToken(token, expiresIn || 300);
        return next.handle(this.getTokenRequest(request, token));
      }),

      catchError((error) => {
        console.error("JWT exchange failed:", error);

        return throwError(() => error);
      }),
    );
  }

  /**
   * Refresh JWT after 401.
   */
  private refreshJwtAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: any,
  ): Observable<HttpEvent<any>> {
    if (this.isRetrying) {
      return throwError(() => originalError);
    }

    this.isRetrying = true;

    this.apigeeAuthService.setToken("", 0);

    return this.getJwtToken().pipe(
      switchMap((tokenResponse: any) => {
        const token = tokenResponse?.access_token;

        const expiresIn = parseInt(tokenResponse?.expires_in || "0", 10);

        if (!token) {
          this.handleUnauthorized();

          return throwError(() => originalError);
        }

        this.apigeeAuthService.setToken(token, expiresIn || 300);

        return next.handle(this.getTokenRequest(request, token));
      }),

      catchError((error) => {
        if (error?.status === 401 || error?.status === 403) {
          this.handleUnauthorized();
        }

        return throwError(() => error);
      }),

      finalize(() => {
        this.isRetrying = false;
      }),
    );
  }

  private isEpcisApi(url: string): boolean {
    return (
      url.includes("/epcis/") ||
      url.includes("epcisvalidator") ||
      url.includes("epcis/records")
    );
  }

  /**
   * Logout on authentication failure.
   */
  private handleUnauthorized(): void {
    this.snackbarService.openSnackbar(
      "The request timed out. Please try again.",
      "sb-failed",
      3000,
    );

    this.apigeeAuthService.setToken("", 0);

    this.authService.logout();
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      request.url.includes("/oauth2/v3/token/jwtforoktab2e") ||
      request.url.includes("/oauth2/v3/token/jwtforoktab2b")
    ) {
      const modifiedRequest = this.getJwtExchangeRequest(request);

      return next.handle(modifiedRequest);
    }
    if (request.url.includes("/oauth2/v3/token/jwttoken")) {
      console.error("❌ OLD JWTTOKEN ENDPOINT IS BEING CALLED:", request.url);

      return next.handle(request);
    }

    if (this.isEpcisApi(request.url)) {
      return this.sendEpcisRequest(request, next);
    }

    const currentToken = this.apigeeAuthService.getCurrentToken();
    if (currentToken && this.apigeeAuthService.isTokenValid()) {
      if (!this.isEpcisApi(request.url) || currentToken.startsWith("eyJ")) {
        return next.handle(this.getTokenRequest(request, currentToken));
      }
    }

    /**
     * For APIs that require the Apigee JWT,
     * obtain it first.
     */
    return this.getJwtToken().pipe(
      switchMap((tokenResponse: any) => {
        const token = tokenResponse?.access_token;

        const expiresIn = parseInt(tokenResponse?.expires_in || "0", 10);

        if (!token) {
          return next.handle(request);
        }

        this.apigeeAuthService.setToken(token, expiresIn || 300);
        return next.handle(this.getTokenRequest(request, token));
      }),
      catchError((error) => {
        console.error("Failed to obtain JWT:", error);
        return throwError(() => error);
      }),
    );
  }
}
